// redux/slices/appointmentHistorySlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { resetStore } from "./resetStore";
import { apiRequest } from "../../Utils/apiClient";
import { getServerUrl } from "../../Utils/serverSelector";
import { getJwtToken } from "../../Utils/authStorage";
import axios from "axios";

const pickError = (error, fallback = "Request failed") =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.response?.data ||
  error?.message ||
  fallback;

export const fetchAppointmentHistoryFileInfo = createAsyncThunk(
  "appointmentHistory/fetchFileInfo",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID мастера не передан");

      const response = await apiRequest(
        "GET",
        `/appointment-history/file-info?uid=${encodeURIComponent(uid)}`
      );

      return response?.data || response;
    } catch (error) {
      if (error?.response?.status === 404) {
        return {
          fileExists: false,
          fileName: null,
          filePath: null,
          size: 0,
          updatedAt: null,
          total: 0,
        };
      }

      return rejectWithValue(
        pickError(error, "Не удалось получить информацию о файле")
      );
    }
  }
);

export const fetchAppointmentHistory = createAsyncThunk(
  "appointmentHistory/fetchAppointments",
  async (
    { uid, search = "", date = "", page = 1, limit = 500 },
    { rejectWithValue }
  ) => {
    try {
      if (!uid) throw new Error("UID мастера не передан");

      const params = new URLSearchParams();

      params.set("uid", String(uid));
      params.set("search", String(search || "").trim());
      params.set("date", String(date || ""));
      params.set("page", String(page || 1));
      params.set("limit", String(limit || 500));

      const response = await apiRequest(
        "GET",
        `/appointment-history?${params.toString()}`
      );

      return response?.data || response;
    } catch (error) {
      return rejectWithValue(
        pickError(error, "Не удалось загрузить старые записи")
      );
    }
  }
);

export const uploadAppointmentHistoryFile = createAsyncThunk(
  "appointmentHistory/uploadFile",
  async ({ uid, file }, { rejectWithValue }) => {
    try {
      if (!uid) {
        throw new Error("UID мастера не передан");
      }

      if (!file) {
        throw new Error("Excel-файл не выбран");
      }

      const formData = new FormData();

      // На backend поле обязательно называется file:
      // upload.single("file")
      formData.append("file", file, file.name);

      const serverUrl = getServerUrl();
      const jwt = await getJwtToken();

      const response = await axios.post(
        `${serverUrl}/appointment-history/upload?uid=${encodeURIComponent(uid)}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ uploadAppointmentHistoryFile:",
        error?.response?.status,
        error?.response?.data || error
      );

      return rejectWithValue(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Не удалось загрузить Excel-файл"
      );
    }
  }
);

export const deleteAppointmentHistoryFile = createAsyncThunk(
  "appointmentHistory/deleteFile",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID мастера не передан");

      const response = await apiRequest(
        "DELETE",
        `/appointment-history/file?uid=${encodeURIComponent(uid)}`
      );

      return response?.data || response;
    } catch (error) {
      return rejectWithValue(
        pickError(error, "Не удалось удалить Excel-файл")
      );
    }
  }
);

const emptyFileInfo = {
  fileExists: false,
  fileName: null,
  filePath: null,
  size: 0,
  updatedAt: null,
  total: 0,
};

const initialState = {
  fileInfo: emptyFileInfo,
  appointments: [],
  total: 0,
  page: 1,
  limit: 500,
  totalPages: 0,
  fileInfoStatus: "idle",
  appointmentsStatus: "idle",
  uploadStatus: "idle",
  deleteStatus: "idle",
  error: null,
};

const appointmentHistorySlice = createSlice({
  name: "appointmentHistory",
  initialState,
  reducers: {
    clearAppointmentHistoryError: (state) => {
      state.error = null;
    },

    clearAppointmentHistorySearch: (state) => {
      state.appointments = [];
      state.total = 0;
      state.page = 1;
      state.totalPages = 0;
      state.appointmentsStatus = "idle";
    },

    resetAppointmentHistoryFile: () => initialState,
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchAppointmentHistoryFileInfo.pending, (state) => {
        state.fileInfoStatus = "loading";
        state.error = null;
      })
      .addCase(fetchAppointmentHistoryFileInfo.fulfilled, (state, action) => {
        state.fileInfoStatus = "succeeded";
        state.fileInfo = {
          ...emptyFileInfo,
          ...(action.payload || {}),
          size: Number(action.payload?.size || 0),
          total: Number(action.payload?.total || 0),
          fileExists: action.payload?.fileExists === true,
        };
      })
      .addCase(fetchAppointmentHistoryFileInfo.rejected, (state, action) => {
        state.fileInfoStatus = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Не удалось получить информацию о файле";
      })

      .addCase(fetchAppointmentHistory.pending, (state) => {
        state.appointmentsStatus = "loading";
        state.error = null;
      })
      .addCase(fetchAppointmentHistory.fulfilled, (state, action) => {
        state.appointmentsStatus = "succeeded";
        state.appointments = Array.isArray(action.payload?.appointments)
          ? action.payload.appointments
          : [];
        state.total = Number(action.payload?.total || 0);
        state.page = Number(action.payload?.page || 1);
        state.limit = Number(action.payload?.limit || 500);
        state.totalPages = Number(action.payload?.totalPages || 0);
      })
      .addCase(fetchAppointmentHistory.rejected, (state, action) => {
        state.appointmentsStatus = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Не удалось загрузить старые записи";
      })

      .addCase(uploadAppointmentHistoryFile.pending, (state) => {
        state.uploadStatus = "loading";
        state.error = null;
      })
      .addCase(uploadAppointmentHistoryFile.fulfilled, (state, action) => {
        state.uploadStatus = "succeeded";
        state.fileInfo = {
          ...emptyFileInfo,
          fileExists: true,
          fileName: action.payload?.fileName || "appointments.xlsx",
          filePath: action.payload?.filePath || null,
          size: Number(action.payload?.size || 0),
          updatedAt: action.payload?.updatedAt || new Date().toISOString(),
          total: Number(action.payload?.total || 0),
        };
        state.appointments = [];
        state.total = 0;
        state.page = 1;
        state.totalPages = 0;
      })
      .addCase(uploadAppointmentHistoryFile.rejected, (state, action) => {
        state.uploadStatus = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Не удалось загрузить Excel-файл";
      })

      .addCase(deleteAppointmentHistoryFile.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(deleteAppointmentHistoryFile.fulfilled, (state) => {
        state.deleteStatus = "succeeded";
        state.fileInfo = { ...emptyFileInfo };
        state.appointments = [];
        state.total = 0;
        state.page = 1;
        state.totalPages = 0;
      })
      .addCase(deleteAppointmentHistoryFile.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Не удалось удалить Excel-файл";
      })

      .addCase(resetStore, () => initialState);
  },
});

export const {
  clearAppointmentHistoryError,
  clearAppointmentHistorySearch,
  resetAppointmentHistoryFile,
} = appointmentHistorySlice.actions;

export default appointmentHistorySlice.reducer;
